import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { RotateCcw, Calendar, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { g2gCategoryConfig } from './constants';

export default function GateToGraveSection({
  isLocked,
  showG2gSection,
  setShowG2gSection,
  g2gSummary,
  g2gSettings,
  counts,
  productLifetime,
  setProductLifetime,
  lifetimeGeography,
  setLifetimeGeography,
  savingG2gSettings,
  onSaveG2gSettings,
  activeTab,
  setActiveTab,
  setNewRow,
  setEditingRowId
}) {
  return (
    <div className="space-y-4">
      {/* G2G Header */}
      <div
        className="flex items-center justify-between p-4 bg-gradient-to-r from-cyan-50 to-rose-50 rounded-xl border border-cyan-200 cursor-pointer"
        onClick={() => setShowG2gSection(!showG2gSection)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cyan-100">
            <RotateCcw className="h-5 w-5 text-cyan-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Gate-to-Grave Assessment</h3>
            <p className="text-sm text-slate-500">Use Phase, Maintenance & End-of-Life</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {g2gSummary && (
            <div className="flex items-center gap-2 text-sm">
              <Badge variant={g2gSummary.use_phase?.complete ? "default" : "outline"} className={g2gSummary.use_phase?.complete ? "bg-cyan-600" : ""}>
                Use: {counts.use_phase}
              </Badge>
              <Badge variant={g2gSummary.maintenance?.complete ? "default" : "outline"} className={g2gSummary.maintenance?.complete ? "bg-orange-600" : ""}>
                Maint: {counts.maintenance}
              </Badge>
              <Badge variant={g2gSummary.eol?.complete ? "default" : "outline"} className={g2gSummary.eol?.complete ? "bg-rose-600" : ""}>
                EoL: {counts.eol}
              </Badge>
            </div>
          )}
          {showG2gSection ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
        </div>
      </div>

      <AnimatePresence>
        {showG2gSection && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4"
          >
            {/* Product Lifetime Setting */}
            <Card className="border-cyan-200 bg-cyan-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-cyan-600" />
                  Product Lifetime (Required)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="text-sm text-slate-600 mb-1 block">Lifetime (years)</label>
                    <Input
                      type="number"
                      value={productLifetime}
                      onChange={(e) => setProductLifetime(e.target.value)}
                      placeholder="e.g., 15"
                      className="max-w-xs"
                      disabled={isLocked}
                      data-testid="product-lifetime-input"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm text-slate-600 mb-1 block">Geography</label>
                    <Select value={lifetimeGeography} onValueChange={setLifetimeGeography} disabled={isLocked}>
                      <SelectTrigger className="max-w-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Global">Global</SelectItem>
                        <SelectItem value="India">India</SelectItem>
                        <SelectItem value="EU">EU</SelectItem>
                        <SelectItem value="US">US</SelectItem>
                        <SelectItem value="China">China</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {!isLocked && (
                    <Button onClick={onSaveG2gSettings} disabled={savingG2gSettings} className="mt-5 bg-cyan-600 hover:bg-cyan-700">
                      {savingG2gSettings ? 'Saving...' : 'Save Lifetime'}
                    </Button>
                  )}
                </div>
                {g2gSettings?.product_lifetime_years && (
                  <p className="text-sm text-emerald-600 mt-2 flex items-center gap-1">
                    <Check className="h-4 w-4" /> Lifetime set to {g2gSettings.product_lifetime_years} years
                  </p>
                )}
              </CardContent>
            </Card>

            {/* G2G Category Tabs */}
            <div className="flex gap-2 p-1.5 bg-gradient-to-r from-cyan-100 to-rose-100 rounded-xl">
              {Object.entries(g2gCategoryConfig).map(([key, cfg]) => {
                const TabIcon = cfg.icon;
                const count = counts[key];
                const isActive = activeTab === key;

                return (
                  <button
                    key={key}
                    onClick={() => { setActiveTab(key); setNewRow(null); setEditingRowId(null); }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                      isActive
                        ? `${cfg.color} border bg-white shadow-sm`
                        : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                    }`}
                    data-testid={`tab-${key}`}
                  >
                    <TabIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">{cfg.label}</span>
                    {count > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-slate-200' : 'bg-slate-200'}`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
